import { Buffer } from 'buffer';

import { FileWithProgress, IP } from '@model/ftf-api.model';
import { FileBasedMailItem, MailItem } from '@utils/office-utils';
import { Observable } from 'rxjs';

import { WordRequestContext } from './office-utils';

/**
 * Converts a base64-encoded string to a Blob.
 * @param base64Data The base64-encoded string to convert.
 * @returns The resulting Blob.
 */
export function base64ToBlob(base64Data: string): Blob {
  const byteCharacters = Buffer.from(base64Data, 'base64').toString('binary'); // Decode base64
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray]); // Create a blob from the byte array
}

/**
 * Converts a base64-encoded string to a File.
 * @param base64Data The base64-encoded string to convert.
 * @param fileName The name of the resulting file.
 * @returns The resulting File.
 */
export function base64ToFile(base64Data: string, fileName: string): File {
  return new File([base64ToBlob(base64Data)], fileName);
}

/**
 * Converts a file or blob to a Blob.
 * @param file The file or blob to convert.
 * @returns The resulting Blob.
 */
export function convertFileToBlob(file: File | Blob): Blob {
  return new Blob([file]);
}

/**
 * Converts a file or blob to a base64-encoded string.
 * @param file The file or blob to convert.
 * @returns An observable that emits the base64-encoded string.
 */
export function convertFileToBase64(file: File | Blob): Observable<string> {
  return new Observable((observer) => {
    const reader = new FileReader();
    reader.onload = () => {
      observer.next((reader.result as string).split(',')[1]);
      observer.complete();
    };
    reader.onerror = observer.error;
    reader.readAsDataURL(file);
  });
}

/**
 * Converts a File to a Buffer.
 * @param file The File to convert.
 * @returns An observable that emits the resulting Buffer.
 */
export function convertFileToBuffer(file: File): Observable<Buffer> {
  return new Observable<Buffer>((observer) => {
    const reader = new FileReader();
    reader.onload = () => {
      observer.next(Buffer.from(reader.result as ArrayBuffer));
      observer.complete();
    };
    reader.onerror = (error) => {
      observer.error(error);
    };
    reader.readAsArrayBuffer(file);
  });
}

export const MAX_FILENAME_LENGTH = 125;
export const MAX_FILE_TITLE_LENGTH = 200;
export const INVALID_FILENAME_CHARS_REGEX = /[\p{Cc}\n\r`*:\\/<>|"?=+#,%\][]+/u;
export const INVALID_FILENAME_CHARS_REGEX_G = new RegExp(INVALID_FILENAME_CHARS_REGEX.source, 'gu');
export const cleanupFilename = (filename: string): string => {
  return filename
    .trim()
    .replaceAll(INVALID_FILENAME_CHARS_REGEX_G, '_')
    .replace(/^[.-]*/, '')
    .replace(/\.*$/g, '');
};

/**
 * Cleans up a file title by trimming whitespace and replacing invalid characters.
 * @param title The title to clean up.
 * @returns The cleaned-up title.
 */
export const cleanupTitle = (title: string): string => {
  return title.trim().replaceAll(/[\p{Cc}"\\]/gu, '_');
};

/**
 * Gets the byte length of a string.
 * @param character The string to measure.
 * @returns The byte length of the string.
 */
export function getByteLength(character: string) {
  const encoder = new TextEncoder(); // Create a new TextEncoder instance
  const encoded = encoder.encode(character); // Encode the character
  return encoded.length; // The length of the Uint8Array gives the byte length
}

/**
 * Truncates a string down to a certain byte length.
 * @param input The string to truncate.
 * @param maxByteLength The maximum byte length of the truncated string.
 * @returns The truncated string.
 */
export function truncateDownToBytes(input: string, maxByteLength: number): string {
  const encoder = new TextEncoder();
  let result = '';
  let accumulatedBytes = 0;

  for (const char of input) {
    const charBytes = encoder.encode(char).length;
    if (accumulatedBytes + charBytes > maxByteLength) break; // Stop if adding this character exceeds maxLength
    result += char;
    accumulatedBytes += charBytes;
  }

  return result;
}

/**
 * Truncates a string down to a certain character length.
 * @param input The string to truncate.
 * @param maxCharLength The maximum character length of the truncated string.
 * @returns The truncated string.
 */
export function truncateDownToCharacters(input: string, maxCharLength: number): string {
  let result = '';
  for (let i = 0; i < maxCharLength && i < input.length; i++) {
    result += input[i];
  }
  return result;
}

/**
 * Gets the file extension from a filename.
 * @param fileName The name of the file.
 * @returns The file extension, or an empty string if none exists.
 */
export function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.replace(/^\./, '') || '';
}

/**
 * Gets the base name (file name without extension) from a filename.
 * @param fileName The name of the file.
 * @returns The base name of the file.
 */
export function getBaseName(fileName: string): string {
  return fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
}

/**
 * Gets the file name from a file path.
 * @param filePath The path of the file.
 * @returns The file name.
 */
export function getFileNameFromPath(filePath: string): string {
  return filePath.split('/').pop() || filePath;
}

/**
 * Clean and truncate a filename to a certain length, preserving the extension.
 * @param fileName
 * @param maxLength
 * @param addRandomDigits
 * @param byteTruncation
 * @param handlePath Whether to handle the path in the string
 */
export function cleanAndTruncateFileName(
  fileName: string,
  maxLength = MAX_FILENAME_LENGTH,
  addRandomDigits = false,
  byteTruncation = true,
  handlePath = true
): string {
  return truncateStringOrFileName(fileName, maxLength, true, true, addRandomDigits, byteTruncation, handlePath);
}

/**
 * Truncate a string to a certain length, preserving the extension if it is a filename.
 * @param input The string to truncate
 * @param maxLength The maximum length of the string
 * @param isFileName Whether the string is a filename
 * @param addEllipsis Whether to add an ellipsis at the end of the truncated string
 * @param appendRandomDigits Whether to add random digits to the filename to prevent duplication
 * @param byteTruncation Whether to truncate by bytes or characters
 * @param handlePath Whether to handle the path in the string
 */
function truncateStringOrFileName(
  input: string | null,
  maxLength: number,
  isFileName = false,
  addEllipsis = false,
  appendRandomDigits = false,
  byteTruncation = true,
  handlePath = true
): string {
  if (input === null || maxLength < 2) {
    return maxLength === 0 ? '' : (input ?? '');
  }

  const path = handlePath ? input.substring(0, input.lastIndexOf('/') + 1) : '';
  const affectedString = isFileName ? cleanupFilename(handlePath ? getFileNameFromPath(input) : input) : input;
  const byteSize = new TextEncoder().encode(affectedString).length;
  if (!appendRandomDigits && byteSize <= maxLength) {
    return (path ? path : '') + affectedString;
  }
  return isFileName
    ? (path ? path : '') + truncateFileName(affectedString, maxLength, addEllipsis, appendRandomDigits, byteTruncation)
    : truncateString(affectedString, maxLength, addEllipsis);
}

/**
 * Truncates a file name to a certain length, preserving the extension.
 * @param fileName The name of the file.
 * @param maxLength The maximum length of the file name.
 * @param addEllipsis Whether to add an ellipsis at the end of the truncated file name.
 * @param appendRandomDigits Whether to append random digits to the file name.
 * @param byteTruncation Whether to truncate by bytes or characters.
 * @returns The truncated file name.
 */
export function truncateFileName(
  fileName: string,
  maxLength: number,
  addEllipsis: boolean,
  appendRandomDigits: boolean,
  byteTruncation = true
): string {
  const byteSize = new TextEncoder().encode(fileName).length;
  if (appendRandomDigits && byteSize + 8 <= maxLength) {
    const base = getBaseName(fileName);
    const eightDigits = generateRandom8DigitNumber().toString();
    const ext = getFileExtension(fileName);
    return `${base}_${eightDigits}.${ext}`;
  }
  const extension = getFileExtension(fileName);
  const trailingNumbers = appendRandomDigits ? generateRandom8DigitNumber().toString() : '';
  const trailingText = getTrailingText(extension, addEllipsis, trailingNumbers);
  if (new TextEncoder().encode(trailingText).length > maxLength) {
    return '.' + extension;
  }

  const baseName = getBaseName(fileName);

  if (appendRandomDigits) {
    const newLength = maxLength - new TextEncoder().encode(trailingText).length;
    return (byteTruncation ? truncateDownToBytes(baseName, newLength) : truncateDownToCharacters(baseName, newLength)) + trailingText;
  } else {
    const trailingText = getTrailingText(extension, addEllipsis);
    const newLength = maxLength - new TextEncoder().encode(trailingText).length;
    return (byteTruncation ? truncateDownToBytes(baseName, newLength) : truncateDownToCharacters(baseName, newLength)) + trailingText;
  }
}

/**
 * Gets the trailing text for a file name.
 * @param extension The file extension.
 * @param addEllipsis Whether to add an ellipsis.
 * @param trailingNumbers The trailing numbers to append.
 * @returns The trailing text.
 */
function getTrailingText(extension: string, addEllipsis = false, trailingNumbers = ''): string {
  const ellipsis = addEllipsis ? '…' : '';
  return `${trailingNumbers ? `_${trailingNumbers}` : ''}${ellipsis}.${extension}`;
}

/**
 * Truncates a string to a certain length.
 * @param input The string to truncate.
 * @param maxLength The maximum length of the string.
 * @param addEllipsis Whether to add an ellipsis at the end of the truncated string.
 * @returns The truncated string.
 */
export function truncateString(input: string, maxLength: number, addEllipsis: boolean): string {
  const ellipsis = addEllipsis ? '…' : '';
  const ellipsisBytes = new TextEncoder().encode(ellipsis).length;
  return addEllipsis ? truncateDownToBytes(input, maxLength - ellipsisBytes) + '…' : truncateDownToBytes(input, maxLength);
}

/**
 * Generates a random 8-digit number.
 * @returns A random 8-digit number.
 */
function generateRandom8DigitNumber(): number {
  return Math.floor(10000000 + Math.random() * 90000000);
}

/**
 * Formats a given timestamp into a trifold date string (YYYY-MM-DD).
 *
 * @param {number | string | Date} input - The Date, epoch milli value, or an ISO formatted string
 *    keep in mind if the input is a string, and its already in YYYY-MM-DD format
 *    calling this is not necissary.
 * @returns {string} The formatted date string in the format YYYY-MM-DD.
 */
export function formatDateYYYYMMDD(input: number | string | Date): string {
  const date = input instanceof Date ? input : new Date(input);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Formats a given timestamp into a date-time string (YYYY-MM-DD HH:MM:SS).
 *
 * @param {number | string | Date} input - The Date, epoch milli value, or an ISO formatted string
 * @returns {string} The formatted date string in the format YYYY-MM-DD HH:MM:SS.
 */
export function formatDateTimeYYYYMMDDHHMMSS(input: number | string | Date): string {
  const date = input instanceof Date ? input : new Date(input);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Gets the icon for a trifold document based on its file name and attachment status.
 * @param fileName The name of the file.
 * @param attachments Whether the file has attachments.
 * @returns The icon name for the trifold document.
 */
export function getTrifoldDocIcon(fileName: string, attachments = false): string {
  let icon = 'file';

  if (/.(pdf)$/.test(fileName)) {
    icon = 'pdf';
  } else if (/.(ppt|pptx|pptm|pps|ppsx|ppsm)$/.test(fileName)) {
    icon = 'ppt';
  } else if (/.(bmp|png|gif|jpg|jpeg|jpe|tif|tiff|eps|tga|ai|svg)$/.test(fileName)) {
    icon = 'image';
  } else if (/.(doc|docx)$/.test(fileName)) {
    icon = 'doc';
  } else if (/.(txt|log)$/.test(fileName)) {
    icon = 'txt';
  } else if (/.(zip|zipx|sit|sitx)$/.test(fileName)) {
    icon = 'zip';
  } else if (/.(rtf|wps|odt|pages|wpd)$/.test(fileName)) {
    icon = 'rtf';
  } else if (/.(xls|xlsx|xlr)$/.test(fileName)) {
    icon = 'xls';
  } else if (/.(csv|ods)$/.test(fileName)) {
    icon = 'bars';
  } else if (/.(htm|html|xhtml)$/.test(fileName)) {
    icon = 'web';
  } else if (/.(msg|eml|emlz)$/.test(fileName)) {
    icon = 'mail';
    if (attachments) {
      icon += '_attachments';
    }
  } else if (/.placeHolder$/.test(fileName)) {
    icon = 'null';
  }

  return icon;
}

/**
 *  Formats a number to a fixed decimal place without rounding.
 * @param number The number to format.
 * @param precision The number of decimal places to keep.
 * @returns The formatted number.
 */
export function toFixedNoRound(number: number, precision: number = 1): number {
  const factor = 10 ** precision;
  return Math.floor(number * factor) / factor;
}

/**
 * Formats a file size into a human-readable string.
 * @param size The file size in bytes.
 * @returns The formatted file size string.
 */
export function formatFileSize(size?: number): string {
  if (!size) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  const precision = unitIndex > 1 ? 1 : 0; // Use 1 decimal place for MB/GB, none for KB/B
  return `${toFixedNoRound(size, precision)} ${units[unitIndex]}`;
}

/**
 * Helper class for creating FileWithProgress objects from various sources.
 */
export class FileWithProgressHelper {
  static lastFileNdx = 5000;

  /**
   * Converts an Outlook item to a FileWithProgress object.
   *
   * @param item - The Outlook item to be converted.
   * @param defaultTrifold - The default trifold value for the `FileWithProgress`. Default value is 'L'.
   * @returns A FileWithProgress object containing the converted data from the Outlook item.
   */
  static fromMailItem(item: MailItem, defaultTrifold: IP.TrifoldEnum = IP.TrifoldEnum.L): FileWithProgress {
    const name = item.fileName ?? cleanAndTruncateFileName(`${item.subject}.eml`, MAX_FILENAME_LENGTH, false, true, false);
    const type = /\.msg$/i.test(name) ? 'application/vnd.ms-outlook' : 'message/rfc822';

    return {
      id: item.itemId!,
      name,
      title: cleanupTitle(item.subject),
      type,
      trifold: defaultTrifold ?? 'L',
      isMainEmail: true,
      referenceType: 1,
      date: item.dateTimeReceived ?? item.dateTimeCreated!,
      urgent: false,
      docNotes: '',
      keywords: '',
      hasAttachments: item.attachments.length > 0,
      isInline: false,
      entry: item instanceof FileBasedMailItem ? 'manual' : 'outlook',
      exportControl: false,
    };
  }

  /**
   * Converts an Outlook attachment to a FileWithProgress object.
   *
   * @param item - The Outlook item containing the attachment.
   * @param defaultTrifold - The default trifold value for the `FileWithProgress`. Default value is 'L'.
   * @param att - The attachment details from Office.
   * @returns A FileWithProgress object representing the attachment, or null if the attachment type is 'cloud'.
   */
  static fromMailAttachment(
    item: MailItem,
    defaultTrifold: IP.TrifoldEnum = IP.TrifoldEnum.L,
    att: Office.AttachmentDetails
  ): FileWithProgress | null {
    let name = cleanupFilename(att.name);
    let type = att.attachmentType;
    const title = cleanupTitle(att.name);
    if (type === 'item') {
      // outlook item attachment
      name = cleanAndTruncateFileName(`${title}.eml`, MAX_FILENAME_LENGTH, false, true, false);
      type = 'message/rfc822';
    } else if (type === 'cloud') {
      // we can't handle cloud attachments
      return null;
    }

    return {
      id: `att-${att.id}`,
      name,
      title,
      type,
      trifold: defaultTrifold ?? 'L',
      date: item.dateTimeReceived ?? item.dateTimeCreated!,
      urgent: false,
      referenceType: 1,
      docNotes: '',
      keywords: '',
      hasAttachments: false,
      isInline: att.isInline ?? false,
      entry: item instanceof FileBasedMailItem ? 'manual' : 'outlook',
      exportControl: false,
    };
  }

  /**
   * Creates a `FileWithProgress` object from a `File` object.
   *
   * @param file - The `File` object to create the `FileWithProgress` from.
   * @param defaultTrifold - The default trifold value for the `FileWithProgress`. Default value is 'L'.
   * @param includeData - Specifies whether to include data in the `FileWithProgress`. Default value is `false`.
   * @returns A new `FileWithProgress` object.
   */
  static fromFile(file: File, defaultTrifold: IP.TrifoldEnum = IP.TrifoldEnum.L, includeData = false): FileWithProgress {
    return {
      id: `file-${this.lastFileNdx++}`,
      name: cleanupFilename(file.name),
      title: cleanupTitle(file.name),
      type: file.type ?? 'application/octet-stream',
      trifold: defaultTrifold ?? 'L',
      referenceType: 1,
      date: new Date(file.lastModified),
      urgent: false,
      docNotes: '',
      keywords: '',
      hasAttachments: false,
      isInline: false,
      entry: 'manual',
      dataFile: includeData ? file : undefined,
      exportControl: false,
    };
  }

  /**
   * Converts a `WordRequestContext` object into a `FileWithProgress` object.
   *
   * @param context - The `WordRequestContext` containing information about the Word document.
   * @param defaultTrifold - (Optional) The default trifold value to use if not provided. Defaults to `IP.TrifoldEnum.L`.
   * @returns A `FileWithProgress` object containing metadata and properties derived from word doc
   */
  static fromWordItem(context: WordRequestContext, defaultTrifold: IP.TrifoldEnum = IP.TrifoldEnum.L): FileWithProgress {
    const fileName = context.fileName ? context.fileName : 'Unknown.docx';
    const title = context.document.properties?.title || fileName;

    return {
      id: `${context.url}`,
      name: cleanupFilename(fileName),
      title: cleanupTitle(title),
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      trifold: defaultTrifold ?? 'L',
      referenceType: 1,
      date: new Date(),
      urgent: false,
      docNotes: '',
      keywords: '',
      hasAttachments: false,
      isInline: false,
      entry: 'word',
      exportControl: false,
    };
  }
}
